// hooks
import { useEffect, useCallback, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import useHttp from "hooks/use-http";
import { useNavigate } from "react-router-dom";

// material styles
import {
  Grid,
  Button,
  TextField,
  FormGroup,
  FormControlLabel,
  Checkbox,
} from "@mui/material";

// material icons
import CheckIcon from "@mui/icons-material/Check";

// request methods
import {
  addEducationRequest,
  getEducationCategoryRequest,
} from "api/requests/educations";

// components
import LoadingCM from "components/shared/Loading";
import DrawerCM from "components/shared/Drawer";
import ProgressBarCM from "components/shared/ProgressBar";
import Upload from "components/shared/Upload";
import UploadVideoCM from "components/shared/Video/Upload";
import ReactHookFormSelect from "components/shared/ReactHookFormSelect";

// helper
import Toast from "helper/toast";

// form validate
import { pageItemSchema, validateImg } from "validations";

// styled component
import {
  StyledStack,
  StyledTitle,
  ButtonContainer,
  StyledMenuItem,
} from "./addEducation.style";

// editor library
import Editor, { buttonList } from "suneditor-react";
import "suneditor/dist/css/suneditor.min.css";
import BackButton from "components/shared/BackButton";

export default function AddEducation() {
  const {
    request,
    loading,
    setLoading,
    isUploading,
    uploadProgress,
    setUploadProgress,
  } = useHttp(false);

  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors },
    getValues,
  } = useForm({
    resolver: yupResolver(pageItemSchema),
  });

  // checkbox state
  const [checked, setChecked] = useState(false);

  const handleCheckChange = () => setChecked((state) => !state);

  // editor value
  const [text, setText] = useState("");

  // upload image and video state
  const [educationImage, setEducationImage] = useState({
    url: null,
    isChanged: false,
  });
  const [educationVideo, setEducationVideo] = useState({
    url: null,
    isChanged: false,
  });

  // text editor value handler method
  const handleChangeText = (text) => setText(text);

  // select option state
  const [categoryList, setCategoryList] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState();

  // add education
  const onSubmit = async (data) => {
    if (!text) return Toast({ type: "empty", title: "فیلد توضیحات " });
    if (!educationImage.url)
      return Toast({ type: "empty", title: "فیلد عکس " });

    const res = await request({
      url: () =>
        addEducationRequest(
          {
            title: data.title,
            category_id: selectedCategory,
            text,
            alt: data.alt,
            meta_description: data.meta_description,
            keyword: data.keyword,
            show_in_home_page: checked ? 1 : 0,
            main_image: educationImage,
            video: educationVideo,
          },
          (progress) => setUploadProgress(progress)
        ),
      isFile: true,
    });
    setLoading(false);
    if (res) {
      Toast({ type: "success", title: "افزودن آموزش" });
      navigate("/education");
    }
  };

  // uploaded image handler method
  const handleChangeEducationImage = (val) => {
    validateImg(val);
    setEducationImage({ url: val, isChanged: true });
  };

  // uploaded video handler method
  const handleChangeEducationVideo = (val) =>
    setEducationVideo({ url: val, isChanged: true });

  // get category
  const getEducationCategoryData = useCallback(async () => {
    setLoading(true);
    const res = await request({
      url: getEducationCategoryRequest,
      values: {
        skip: 0,
        limit: 100,
        search: null,
      },
      isMulty: true,
    });
    setLoading(false);

    if (res) {
      let transformedData = res.data?.data.map((items) => ({
        id: items.id,
        title: items.title,
      }));
      setCategoryList(transformedData);
    }
  }, [request]);

  useEffect(() => {
    getEducationCategoryData();
  }, [getEducationCategoryData]);

  return (
    <DrawerCM title="افزودن آموزش">
      {isUploading ? (
        <ProgressBarCM value={uploadProgress} />
      ) : loading ? (
        <LoadingCM />
      ) : (
        <Grid container>
          <Grid item mt={3} xs={12}>
            <BackButton Route="/education" />
          </Grid>
          <StyledStack>
            <StyledTitle>افزودن آموزش</StyledTitle>
            <Grid container>
              <Grid item md={4} xs={12} p={1.2}>
                <TextField
                  label="عنوان آموزش"
                  variant="outlined"
                  fullWidth
                  id="title"
                  {...register("title")}
                  error={Boolean(errors.title)}
                  helperText={errors.title?.message}
                />
              </Grid>

              <Grid item md={4} xs={12} p={1.2}>
                <ReactHookFormSelect
                  label="سردسته"
                  control={control}
                  fullWidth
                  title="سردسته"
                  id="category"
                  {...register("category")}
                  error={Boolean(errors.category)}
                  helperText={errors.category?.message}
                  onChange={(value) => setSelectedCategory(value)}
                >
                  {categoryList?.map((item, index) => (
                    <StyledMenuItem key={index} value={item.id}>
                      {item.title}
                    </StyledMenuItem>
                  ))}
                </ReactHookFormSelect>
              </Grid>
              <Grid item md={4} xs={12} p={1.2} sx={{ pt: 2.2 }}>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={checked}
                        onChange={handleCheckChange}
                      />
                    }
                    label="نمایش در صفحه اصلی"
                  />
                </FormGroup>
              </Grid>
              <Grid item md={6} xs={12} p={1.2}>
                {educationImage.url ? (
                  <Upload
                    isWeb={educationImage.isWeb}
                    isLocal={!educationImage.isWeb}
                    file={educationImage.url}
                    refillable
                    type="add-image"
                    onChange={handleChangeEducationImage}
                    width={"100%"}
                    height={"100%"}
                    objectFit="contain"
                  />
                ) : (
                  <Upload
                    isLocal={educationImage.isChanged}
                    file={educationImage.url}
                    refillable
                    type="add-image"
                    onChange={handleChangeEducationImage}
                    width={"100%"}
                    height={"100%"}
                    objectFit="contain"
                  />
                )}
              </Grid>
              <Grid item md={6} xs={12} p={1.2}>
                {educationVideo.url ? (
                  <UploadVideoCM
                    isWeb={educationVideo.isWeb}
                    isLocal={!educationVideo.isWeb}
                    file={educationVideo.url}
                    refillable
                    type="add-video"
                    onChange={handleChangeEducationVideo}
                    width={"100%"}
                    height={"100%"}
                  />
                ) : (
                  <UploadVideoCM
                    isLocal={educationVideo.isChanged}
                    file={educationVideo.url}
                    refillable
                    type="add-video"
                    onChange={handleChangeEducationVideo}
                    width={"100%"}
                    height={"100%"}
                  />
                )}
              </Grid>
              <Grid item md={6} xs={12} p={1.2}>
                <TextField
                  label=" متن جایگزین"
                  variant="outlined"
                  fullWidth
                  id="alt"
                  {...register("alt")}
                  error={Boolean(errors.alt)}
                  helperText={errors.alt?.message}
                />
              </Grid>
              <Grid item md={6} xs={12} p={1.2}>
                <TextField
                  label="کلمه کلیدی"
                  variant="outlined"
                  fullWidth
                  id="keyword"
                  {...register("keyword")}
                  error={Boolean(errors.keyword)}
                  helperText={errors.keyword?.message}
                />
              </Grid>
              <Grid item xs={12} p={1.2}>
                <TextField
                  label="فراتوضیحات"
                  variant="outlined"
                  fullWidth
                  id="meta_description"
                  {...register("meta_description")}
                  error={Boolean(errors.meta_description)}
                  helperText={errors.meta_description?.message}
                />
              </Grid>
              <Grid item xs={12} p={1.2}>
                <Editor
                  enableToolbar={true}
                  onChange={handleChangeText}
                  defaultValue={text}
                  setDefaultStyle="font-family: IranSans; height: 40vh;"
                  setOptions={{
                    buttonList: buttonList.complex,
                    font: [
                      "IranSans",
                      "IranSansBold",
                      "Arial",
                      "Impact",
                      "Tahoma",
                    ],
                  }}
                />
              </Grid>

              <ButtonContainer>
                <Button
                  startIcon={<CheckIcon />}
                  variant="contained"
                  sx={{ mt: 1 }}
                  onClick={handleSubmit(onSubmit)}
                >
                  ذخیره
                </Button>
              </ButtonContainer>
            </Grid>
          </StyledStack>
        </Grid>
      )}
    </DrawerCM>
  );
}
